<template>
  <div class="home">
    <!-- Блок с цитатой -->
    <div class="quote-block" ref="quoteBlock">
      <div class="quote-content">
        <p class="quote-text">
          "Многое из математики не остаётся в памяти, но когда поймёшь её, тогда легко при случае вспомнить забытое."
        </p>
        <p class="quote-author">— М.В. Остроградский</p>
      </div>
      <svg class="shape left" viewBox="0 0 100 100" width="100" height="100" overflow="visible">
        <path class="triangle" d="M50 10 L90 90 L10 90 Z" />
      </svg>
    </div>

    <!-- Блок с информацией и фото -->
    <div class="info-block" ref="infoBlock">
      <div class="info-content">
        <div class="info-text">
          <h1>Добро пожаловать!</h1>
          <h2>Учитель математики Гайдук Янина Сергеевна, Краснодар</h2>
          <p>Я рада приветствовать Вас на своём сайте.</p>
          <p>На страницах сайта Вы найдёте следующую информацию:</p>
          <ul class="info-list">
            <li>КТП и рабочие программы по математике, алгебре, геометрии, практикуму по математике;</li>
            <li>Планирование специальных курсов, математических кружков;</li>
            <li>Разработки уроков;</li>
            <li>Разработки внеклассных мероприятий;</li>
            <li>Задания олимпиад, контрольных работ;</li>
            <li>Подготовка к ГИА и ЕГЭ;</li>
            <li>И многое другое…</li>
          </ul>
          <p class="info-message">
            Уважаемые гости моего сайта! Пожалуйста, оставьте свои комментарии в гостевой книге. Мне интересно знать Ваше мнение о сайте, а также вопросы, которые Вам интересны.
          </p>
          <p class="info-signature">Гайдук Янина Сергеевна</p>
        </div>
        <div class="photo-container">
          <img src="@/assets/photo_yanina_gaiduk.jpg" alt="Фотография учителя" class="teacher-photo">
        </div>
      </div>
      <svg class="shape right" viewBox="0 0 100 100" width="100" height="100" overflow="visible">
        <path class="star" d="M50 10 L61 35 L88 35 L68 55 L79 80 L50 65 L21 80 L32 55 L12 35 L39 35 Z" />
      </svg>
    </div>

    <!-- Карточки -->
    <div class="cards">
      <div
          class="card"
          v-for="(card, index) in cards"
          :key="index"
          @click="navigateTo(card.link)"
          :ref="el => cardRefs[index] = el"
      >
        <h2>{{ card.title }}</h2>
        <p>{{ card.description }}</p>
        <svg class="shape" :class="card.position" viewBox="0 0 100 100" width="100" height="100" overflow="visible">
          <path :d="card.shape" :transform="card.rotation" />
        </svg>
      </div>
    </div>

    <!-- Блок с отзывами -->
    <div class="reviews-block" ref="reviewsBlock">
      <h2 class="reviews-title">Отзывы</h2>
      <swiper
          :modules="modules"
          :slides-per-view="slidesPerView"
          :space-between="30"
          :pagination="{ clickable: true, el: '.swiper-pagination' }"
          @swiper="onSwiper"
          @slideChange="onSlideChange"
      >
        <swiper-slide v-for="(review, index) in reviews" :key="index">
          <div class="review-card">
            <p class="review-text">{{ review.text }}</p>
            <div class="review-footer">
              <p class="review-date">{{ formatDate(review.date) }}</p>
              <p class="review-author">{{ review.author }}</p>
            </div>
          </div>
        </swiper-slide>

        <!-- Слайд для добавления отзыва -->
        <swiper-slide>
          <div class="add-review-card" @click="showReviewModal = true">
            <span>+</span>
            <p>Оставить отзыв</p>
          </div>
        </swiper-slide>

        <div class="swiper-pagination"></div>
      </swiper>
    </div>

    <!-- Модальное окно для отзыва -->
    <div v-if="showReviewModal" class="review-modal">
      <div class="modal-content">
        <span class="close" @click="closeModal">&times;</span>
        <h3>Оставить отзыв</h3>
        <form @submit.prevent="submitReview">
          <div class="form-group">
            <label>Ваше имя:</label>
            <input
                type="text"
                v-model="newReview.author"
                placeholder="Иван Иванов"
                :class="{ 'error': errors.author }"
                @input="handleInput('author')"
            >
            <span v-if="errors.author" class="error-text">{{ errors.author }}</span>
          </div>
          <div class="form-group">
            <label>Ваш отзыв:</label>
            <textarea
                v-model="newReview.text"
                placeholder="Поделитесь вашим мнением..."
                rows="4"
                :class="{ 'error': errors.text }"
                @input="handleInput('text')"
            ></textarea>
            <span v-if="errors.text" class="error-text">{{ errors.text }}</span>
          </div>
          <button
              type="submit"
              class="submit-btn"
              :style="buttonStyle"
          >
            Отправить
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { gsap } from 'gsap';
import store from "@/store";
import {toast} from "vue3-toastify";

export default {
  name: 'MainContent',
  components: {
    Swiper,
    SwiperSlide,
  },
  data() {
    return {
      cards: [
        {
          title: 'О себе',
          description: 'Узнайте больше обо мне',
          link: '/about',
          shape: 'M50 10 L90 90 L10 90 Z',
          rotation: 'rotate(45, 50, 50)',
          position: 'left',
        },
        {
          title: 'Материалы',
          description: 'Учебные материалы и ресурсы',
          link: '/materials',
          shape: 'M50 10 L80 30 L80 70 L50 90 L20 70 L20 30 Z',
          rotation: 'rotate(45, 50, 50)',
          position: 'right',
        },
        {
          title: 'Связь со мной',
          description: 'Свяжитесь со мной',
          link: '/contact',
          shape: 'M50 10 A40 40 0 1 1 50 90 A40 40 0 1 1 50 10 Z',
          rotation: '',
          position: 'left',
        },
      ],
      reviews: [],
      slidesPerView: 3,
      modules: [Pagination],
      cardRefs: [],
      observers: [],
      showReviewModal: false,
      newReview: {
        author: '',
        text: '',
        date: new Date().toISOString()
      },
      errors: {
        author: '',
        text: ''
      },
      formSubmitted: false,
      isLoading: false,
    };
  },
  computed: {
    isFormValid() {
      return (
          this.newReview.author.trim().length >= 2 &&
          this.newReview.text.trim().length >= 10
      );
    },
    buttonStyle() {
      return {
        backgroundColor: this.isFormValid ? '#007aff' : 'rgba(100, 150, 255, 0.8)',
        transition: 'background-color 0.3s ease'
      };
    }
  },
  methods: {
    navigateTo(link) {
      this.$router.push(link);
    },
    onSwiper(swiper) {
      console.log(swiper);
    },
    onSlideChange() {
      console.log('slide change');
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const options = { day: 'numeric', month: 'long', year: 'numeric' };
      return date.toLocaleDateString('ru-RU', options);
    },
    updateSlidesPerView() {
      this.slidesPerView = window.innerWidth < 768 ? 1 : 3;
    },
    initAnimations() {
      this.setupObserver(this.$refs.quoteBlock, '.triangle');
      this.setupObserver(this.$refs.infoBlock, '.star');
      this.setupObserver(this.$refs.reviewsBlock);

      this.cardRefs.forEach((card, index) => {
        this.setupCardObserver(card, index);
      });
    },
    setupObserver(element, shapeSelector = null) {
      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            gsap.to(entry.target, {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power2.out'
            });

            if (shapeSelector) {
              const shape = entry.target.querySelector(shapeSelector);
              this.animateShape(shape, entry.target, false);
            }
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      if (element) {
        observer.observe(element);
        this.observers.push(observer);
      }
    },
    setupCardObserver(card, index) {
      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            gsap.to(entry.target, {
              opacity: 1,
              y: 0,
              duration: 0.5,
              delay: index * 0.1,
              ease: 'power2.out'
            });

            const shape = entry.target.querySelector('path');
            this.animateShape(shape, entry.target, true);
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      if (card) {
        observer.observe(card);
        this.observers.push(observer);
      }
    },
    animateShape(shape, parent, isCard = false) {
      if (!shape || shape.dataset.animated) return;
      shape.dataset.animated = 'true';

      const length = shape.getTotalLength();
      shape.style.strokeDasharray = length;
      shape.style.strokeDashoffset = length;

      gsap.to(shape, {
        strokeDashoffset: 0,
        duration: 2,
        ease: 'power2.out',
        onComplete: () => {
          if (isCard) {
            gsap.to(parent.querySelector('.shape'), {
              x: () => gsap.utils.random(-10, 10),
              y: () => gsap.utils.random(-5, 5),
              duration: () => gsap.utils.random(3, 5),
              repeat: -1,
              yoyo: true,
              ease: 'sine.inOut'
            });
          } else {
            gsap.to(shape, {
              rotation: 360,
              duration: 10,
              repeat: -1,
              ease: 'linear',
              transformOrigin: '50% 50%'
            });
          }
        }
      });
    },
    handleInput(field) {
      if (this.formSubmitted) {
        this.validateField(field);
      }
    },
    validateField(field) {
      const value = this.newReview[field].trim();
      let error = '';

      switch(field) {
        case 'author':
          error = value.length >= 2 ? '' : 'Имя должно содержать минимум 2 символа';
          break;
        case 'text':
          error = value.length >= 10 ? '' : 'Отзыв должен содержать минимум 10 символов';
          break;
      }

      this.errors = { ...this.errors, [field]: error };
    },
    validateForm() {
      this.validateField('author');
      this.validateField('text');
      return this.isFormValid;
    },
    submitReview() {
      this.formSubmitted = true;
      this.showLoadingToast()

      if (!this.validateForm()) {
        return;
      }
      let report = {};
      report['name'] = this.newReview.author
      report['comment'] = this.newReview.text
      report['date'] = this.newReview.date

      store.dispatch('baseService/sendNewReport',  report) .then(
          () => {
            this.hideLoadingToast()
            this.showSuccessToast()
          },
          error => {
            console.log(error)
            this.hideLoadingToast()
            this.showErrorToast()
          }
      );
      //this.reviews.unshift({...this.newReview});
      this.closeModal();
    },
    closeModal() {
      this.showReviewModal = false;
      this.newReview = { author: '', text: '', date: new Date().toISOString() };
      this.errors = { author: '', text: '' };
      this.formSubmitted = false;
    },
    showSuccessToast() {
      toast("Отзыв успешно отправлен! Дождитесь модерации.", { type: "success" });
    },
    showErrorToast() {
      toast("Ошибка связи с сервером:(", { type: "error" });
    },
    showLoadingToast() {
      this.isLoading = true;
      this.loadingToastId = toast.loading("Отправляю данные...", {
        type: "info",
        position: "top-center",
        autoClose: false
      });
    },

    hideLoadingToast() {
      this.isLoading = false;
      toast.remove(this.loadingToastId); // Закрываем уведомление по ID
    },
  },
  mounted() {
    // Загрузка отзывов с бэкенда
    store.dispatch('baseService/getAllReports').then(
        response => {
          console.log(response)
          this.reviews = response.map(report => ({
            id : report.id,
            text: report.comment,
            author: report.name,
            date: report.date
          }));
        },
        error => {
          console.log(error)

        }
    )
    this.updateSlidesPerView();
    window.addEventListener('resize', this.updateSlidesPerView);
    this.$nextTick(() => {
      this.initAnimations();
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateSlidesPerView);
    this.observers.forEach(observer => observer.disconnect());
  },
};
</script>

<style scoped>
.home {
  text-align: center;
  padding: 20px;
  position: relative;
  overflow: hidden;
  color: #333;
}

.quote-block,
.info-block,
.reviews-block,
.card {
  opacity: 0;
  transform: translateY(30px);
}

.quote-block {
  background: #f9f9f9;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  margin: 0 auto 40px;
  max-width: 1000px;
  padding: 20px;
  position: relative;
  z-index: 1;
}

.quote-content {
  text-align: left;
}

.quote-text {
  font-size: 16px;
  font-style: italic;
  color: #333;
  margin-bottom: 10px;
}

.quote-author {
  font-size: 14px;
  font-weight: bold;
  color: #555;
  text-align: right;
  margin-top: 10px;
}

.info-block {
  background: #f9f9f9;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  margin: 0 auto 40px;
  max-width: 1000px;
  padding: 20px;
  position: relative;
  z-index: 1;
}

.info-content {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.info-text {
  flex: 1;
  text-align: left;
}

.info-text h1 {
  font-size: 28px;
  margin-bottom: 10px;
}

.info-text h2 {
  font-size: 22px;
  margin-bottom: 15px;
  color: #555;
}

.info-text p {
  font-size: 16px;
  margin-bottom: 15px;
}

.info-list {
  list-style-type: disc;
  padding-left: 40px;
  margin-bottom: 20px;
}

.info-list li {
  font-size: 14px;
  margin-bottom: 10px;
}

.info-message {
  font-style: italic;
  font-size: 14px;
  color: #555;
}

.info-signature {
  font-weight: bold;
  text-align: right;
  font-size: 16px;
}

.photo-container {
  width: 300px;
  height: 300px;
  overflow: hidden;
  border-radius: 50% 0 0 50%;
  position: relative;
  margin-left: auto;
  margin-right: -20px;
}

.teacher-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
}

.teacher-photo:hover {
  transform: scale(1.05);
}

.cards {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 0 auto 40px;
  max-width: 1000px;
  flex-wrap: wrap;
}

.card {
  background: #f9f9f9;
  border-radius: 10px;
  padding: 20px;
  width: calc(33.333% - 40px);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  color: #333;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: visible;
}

.card:hover {
  transform: translateY(-10px) scale(1.05);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.shape {
  position: absolute;
  width: 120px;
  height: 120px;
  z-index: -1;
  transform-box: fill-box;
}

.shape path {
  stroke: rgba(100, 150, 255, 0.5);
  stroke-width: 2;
  fill: none;
  transition: stroke 0.3s ease;
}

.shape.left {
  left: -50px;
  bottom: -50px;
}

.shape.right {
  right: -50px;
  bottom: -50px;
}

.card:hover .shape path {
  stroke: rgba(100, 150, 255, 0.8);
}

.reviews-block {
  margin: 40px auto;
  max-width: 1000px;
}

.reviews-title {
  text-align: left;
  font-size: 24px;
  margin-bottom: 20px;
}

.review-card {
  background: #f9f9f9;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  text-align: left;
  height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.add-review-card {
  background: #f9f9f9;
  border: 2px dashed #ccc;
  border-radius: 10px;
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 20px;
}

.add-review-card:hover {
  border-color: #888;
  background: #f5f5f5;
}

.add-review-card span {
  font-size: 40px;
  color: #666;
  margin-bottom: 10px;
}

.add-review-card p {
  color: #666;
  font-weight: 500;
  text-align: center;
}

.swiper-pagination {
  position: relative;
  margin-top: 20px;
}

.swiper-pagination-bullet {
  width: 10px;
  height: 10px;
  background: #ccc;
  opacity: 1;
}

.swiper-pagination-bullet-active {
  background: #555;
}

.review-text {
  font-size: 16px;
  margin-bottom: 10px;
  flex-grow: 1;
}

.review-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.review-date {
  font-size: 14px;
  color: #777;
}

.review-author {
  font-size: 14px;
  font-weight: bold;
  color: #555;
  text-align: right;
}

.review-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 15px;
  width: 90%;
  max-width: 500px;
  position: relative;
  animation: modalEnter 0.3s ease;
}

.close {
  position: absolute;
  right: 20px;
  top: 15px;
  font-size: 28px;
  cursor: pointer;
  color: #666;
}

.close:hover {
  color: #333;
}

h3 {
  margin-bottom: 20px;
  color: #333;
  text-align: center;
}

.form-group {
  margin-bottom: 20px;
  width: 100%;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

label {
  display: block;
  margin-bottom: 8px;
  color: #555;
  font-weight: 500;
  text-align: left;
}

input, textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

input:focus, textarea:focus {
  outline: none;
  border-color: #888;
  box-shadow: 0 0 5px rgba(136, 136, 136, 0.2);
}

input.error,
textarea.error {
  border-color: #ff4444;
  box-shadow: 0 0 5px rgba(255, 68, 68, 0.2);
}

input:not(.error),
textarea:not(.error) {
  border-color: #888;
  box-shadow: 0 0 5px rgba(136, 136, 136, 0.2);
}

.error-text {
  display: block;
  color: #ff4444;
  font-size: 12px;
  margin-top: 5px;
  text-align: left;
  animation: fadeIn 0.3s ease;
}

textarea {
  resize: vertical;
}

.submit-btn {
  background-color: rgba(100, 150, 255, 0.8);
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  width: 100%;
  max-width: 400px;
  margin: 20px auto 0;
  display: block;
  transition: background-color 0.3s ease;
}

.submit-btn:hover {
  background-color: rgba(80, 130, 235, 0.8);
}

@keyframes modalEnter {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .quote-block,
  .info-block {
    padding: 15px;
  }

  .quote-text {
    font-size: 14px;
  }

  .quote-author {
    font-size: 12px;
  }

  .info-content {
    flex-direction: column;
  }

  .info-text h1 {
    font-size: 24px;
  }

  .info-text h2 {
    font-size: 18px;
  }

  .info-text p,
  .info-list li {
    font-size: 14px;
  }

  .photo-container {
    width: 250px;
    height: 250px;
    margin-right: 0;
    order: -1;
  }

  .card {
    width: 100%;
    max-width: 300px;
  }

  .reviews-title {
    font-size: 20px;
  }
  .add-review-card{
    height: 150px;
    padding: 15px;
  }
  .review-card {
    height: 150px;
    padding: 15px;
  }

  .review-text {
    font-size: 14px;
  }

  .modal-content {
    padding: 20px;
  }

  input, textarea {
    padding: 10px;
    font-size: 14px;
  }

  .submit-btn {
    padding: 10px 20px;
    font-size: 14px;
  }
}
</style>